import React, { useState, useEffect } from 'react';
import { Framework } from '../types/grc';
import { grcService } from '../services/grcService';
import { FrameworkSelector } from '../components/grc/FrameworkSelector';
import { ComplianceWizard } from '../components/grc/ComplianceWizard';
import { FrameworkDashboard } from '../components/grc/FrameworkDashboard';
import { FrameworkUploader } from '../components/grc/FrameworkUploader';
import { GRCAnalytics } from '../components/grc/GRCAnalytics';
import { Header } from '../components/layout/Header';
import { 
  ArrowLeft, Database, 
  AlertTriangle, CheckCircle, TrendingUp, BarChart3 
} from 'lucide-react';

export const GRC: React.FC = () => {
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedFramework, setSelectedFramework] = useState<Framework | null>(null);
  const [view, setView] = useState<'selector' | 'wizard' | 'dashboard' | 'uploader' | 'analytics'>('selector');
  const [loading, setLoading] = useState(true);
  const [globalStats, setGlobalStats] = useState<any>(null);

  useEffect(() => {
    loadFrameworks();
    loadGlobalStats();
  }, []);

  const loadFrameworks = async () => {
    setLoading(true);
    const fws = await grcService.getFrameworksWithStatus();
    setFrameworks(fws);
    setLoading(false);
  };

  const loadGlobalStats = async () => {
    const stats = await grcService.getDashboard();
    setGlobalStats(stats);
  };

  const handleSelectFramework = (fw: Framework) => {
    setSelectedFramework(fw);
    if (fw.wizard_completed && fw.dashboard_enabled) {
      setView('dashboard');
    } else {
      setView('wizard');
    }
  };

  const handleWizardComplete = async () => {
    // Reload frameworks to get updated status
    await loadFrameworks();
    if (selectedFramework) {
      // Re-fetch the framework to get updated status
      const updated = frameworks.find(f => f.id === selectedFramework.id);
      if (updated) {
        setSelectedFramework(updated);
      }
    }
    setView('dashboard');
  };

  const handleWizardCancel = () => {
    setSelectedFramework(null);
    setView('selector');
  };

  const handleResetWizard = async () => {
    if (!selectedFramework) return;
    if (!confirm('Ovo će resetovati wizard i obrisati sve postavke primenjivosti. Nastaviti?')) return;

    await grcService.resetWizard(selectedFramework.id);
    await loadFrameworks();
    setView('wizard');
  };

  const handleBackToSelector = () => {
    setSelectedFramework(null);
    setView('selector');
  };

  const handleAddNewFramework = () => {
    setView('uploader');
  };

  const handleUploadComplete = async (frameworkId: number) => {
    // Reload frameworks and select the new one
    await loadFrameworks();
    const newFw = frameworks.find(f => f.id === frameworkId);
    if (newFw) {
      setSelectedFramework(newFw);
      setView('wizard');
    } else {
      // Framework might not be in state yet, reload
      const fws = await grcService.getFrameworksWithStatus();
      const found = fws.find(f => f.id === frameworkId);
      if (found) {
        setFrameworks(fws);
        setSelectedFramework(found);
        setView('wizard');
      } else {
        setView('selector');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      <Header 
        title="GRC Module" 
        subtitle={selectedFramework?.name || (view === 'analytics' ? 'Cross-Framework Analytics' : undefined)}
      />

      {/* Sub-header with navigation and stats */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {view !== 'selector' && view !== 'analytics' && (
                <button
                  onClick={handleBackToSelector}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Frameworks
                </button>
              )}
              {view === 'analytics' && (
                <button
                  onClick={() => setView('selector')}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Frameworks
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setView('analytics')}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  view === 'analytics' 
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm' 
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                Cross-Framework Analytics
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Framework Selector View */}
            {view === 'selector' && (
              <div className="space-y-8">
                {/* Global Stats */}
                {globalStats && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">
                            {globalStats.total_controls || 0}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Ukupno Kontrola</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {globalStats.compliant || 0}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Usklađeno</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {globalStats.open_risks || 0}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Otvoreni Rizici</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 shadow-sm text-white">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                          <TrendingUp className="w-6 h-6" />
                        </div>
                        <div>
                          <div className="text-2xl font-bold">
                            {globalStats.compliance_score || 0}%
                          </div>
                          <div className="text-sm text-indigo-100">Compliance Score</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Framework Selection */}
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Izaberite Framework
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    Kliknite na framework da pokrenete wizard za procenu primenjivosti ili otvorite dashboard.
                  </p>
                  <FrameworkSelector
                    frameworks={frameworks}
                    selectedId={selectedFramework?.id || null}
                    onSelect={handleSelectFramework}
                    onAddNew={handleAddNewFramework}
                  />
                </div>
              </div>
            )}

            {/* Wizard View */}
            {view === 'wizard' && selectedFramework && (
              <div className="max-w-3xl mx-auto">
                <ComplianceWizard
                  framework={selectedFramework}
                  onComplete={handleWizardComplete}
                  onCancel={handleWizardCancel}
                />
              </div>
            )}

            {/* Dashboard View */}
            {view === 'dashboard' && selectedFramework && (
              <FrameworkDashboard
                framework={selectedFramework}
                onResetWizard={handleResetWizard}
              />
            )}

            {/* Uploader View */}
            {view === 'uploader' && (
              <FrameworkUploader
                onUploadComplete={handleUploadComplete}
                onCancel={handleBackToSelector}
              />
            )}

            {/* Analytics View */}
            {view === 'analytics' && (
              <GRCAnalytics />
            )}
          </>
        )}
      </main>
    </div>
  );
};
